import { PropertyValueMap, html } from "lit";
import { customElement, query, property } from "lit/decorators.js";
import {
    OrientationResult,
    registerOrientationListener,
    unregisterOrientationListener,
} from "../../Orientation";
import { BaseElement } from "../BaseElement";
import { styles } from "./Compass.style";
import { watch } from "../internal/watch";

const SVG_NS = "http://www.w3.org/2000/svg";

const TICK_OUTER = 30;
const TICK_MAJOR_HEIGHT = 20;
const TICK_MINOR_HEIGHT = 15;
const POINTER_HEIGHT = TICK_MAJOR_HEIGHT;
const POINTER_WIDTH = 25;

const TICK_MINOR_INNER = TICK_OUTER + TICK_MINOR_HEIGHT;
const TICK_MAJOR_INNER = TICK_OUTER + TICK_MAJOR_HEIGHT;
const POINTER_Y_OFFSET = TICK_OUTER;
const POINTER_X_OFFSET = 150;
const POINTER_INNER = POINTER_Y_OFFSET + POINTER_HEIGHT;
const POINTER_HALF_WIDTH = POINTER_WIDTH / 2;

@customElement("ds-compass")
export class Compass extends BaseElement {
    static override styles = [styles];

    @property({ type: Number }) pointerHeading = 0;

    @query("#compass-svg") protected _compassElement: SVGElement | undefined;
    @query("#pointer-svg") protected _pointerElement: SVGElement | undefined;
    @query("#compass") protected _compassContainer: SVGElement | undefined;

    private _width = 300;
    private _halfWidth = this._width / 2;
    private _halfWidthStr = this._halfWidth.toString();

    @watch("pointerHeading")
    pointerHeadingChanged() {
        if (this._pointerElement) {
            this._pointerElement.style.transform = `rotate(${this.pointerHeading}deg)`;
        }
    }

    private _orientationListenerId: string | undefined;

    constructor() {
        super();
    }

    override async connectedCallback(): Promise<void> {
        super.connectedCallback();

        this._orientationListenerId = registerOrientationListener((result: OrientationResult) =>
            this._compassHandler(result)
        );
    }

    protected override firstUpdated(
        _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>
    ): void {
        this._createCompassSVG();
        this._createPointerSVG();
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this._orientationListenerId) {
            unregisterOrientationListener(this._orientationListenerId);
            this._orientationListenerId = undefined;
        }
    }

    private _compassHandler(result: OrientationResult) {
        if (this._compassContainer) {
            this._compassContainer.style.transform = result.transform;
        }
    }

    private _drawCenterLine(x1: number, y1: number, x2: number, y2: number) {
        const centreLineHorizontal = document.createElementNS(SVG_NS, "line");
        centreLineHorizontal.setAttributeNS(null, "x1", x1.toString());
        centreLineHorizontal.setAttributeNS(null, "y1", y1.toString());
        centreLineHorizontal.setAttributeNS(null, "x2", x2.toString());
        centreLineHorizontal.setAttributeNS(null, "y2", y2.toString());
        centreLineHorizontal.setAttributeNS(null, "stroke", "grey");
        centreLineHorizontal.setAttributeNS(null, "stroke-width", "1");
        centreLineHorizontal.setAttributeNS(null, "stroke-opacity", "0.5");
        this._compassElement?.appendChild(centreLineHorizontal);
    }

    private _drawCardinalDirection(x: number, y: number, displayText: string) {
        const direction = document.createElementNS(SVG_NS, "text");
        direction.setAttributeNS(null, "x", x.toString());
        direction.setAttributeNS(null, "y", y.toString());
        direction.setAttributeNS(null, "font-size", "20px");
        direction.setAttributeNS(null, "font-family", "Helvetica");
        direction.setAttributeNS(null, "fill", "white");
        const textNode = document.createTextNode(displayText);
        direction.appendChild(textNode);
        this._compassElement?.appendChild(direction);
    }
    private _createPointerSVG() {
        if (this._pointerElement) {
            const pointer = document.createElementNS(SVG_NS, "polygon");
            pointer.setAttributeNS(
                null,
                "points",
                `${POINTER_X_OFFSET},${POINTER_Y_OFFSET} ${
                    POINTER_X_OFFSET + POINTER_HALF_WIDTH
                },${POINTER_INNER} ${POINTER_X_OFFSET - POINTER_HALF_WIDTH}, ${POINTER_INNER}`
            );
            pointer.setAttributeNS(null, "fill", "red");
            this._pointerElement.appendChild(pointer);
        }
    }

    private _createCompassSVG() {
        if (this._compassElement) {
            const c = document.createElementNS(SVG_NS, "circle");
            c.setAttributeNS(null, "cx", this._halfWidth.toString());
            c.setAttributeNS(null, "cy", this._halfWidth.toString());
            c.setAttributeNS(null, "r", "20");
            c.setAttributeNS(null, "fill", "white");
            c.setAttributeNS(null, "fill-opacity", "0.1");
            this._compassElement.appendChild(c);

            this._drawCenterLine(this._halfWidth, 100, this._halfWidth, 200);
            this._drawCenterLine(100, this._halfWidth, 200, this._halfWidth);
            this._drawCardinalDirection(143, 72, "N");
            this._drawCardinalDirection(228, 158, "E");
            this._drawCardinalDirection(143, 242, "S");
            this._drawCardinalDirection(58, 158, "W");

            for (let i = 0; i < 360; i += 2) {
                // draw degree lines
                let s = "grey";
                let y2 = 0,
                    w = 0;
                if (i == 0 || i % 30 == 0) {
                    w = 3;
                    s = "white";
                    y2 = TICK_MAJOR_INNER;
                } else {
                    w = 1;
                    y2 = TICK_MINOR_INNER;
                }

                const l1 = document.createElementNS(SVG_NS, "line");
                l1.setAttributeNS(null, "x1", this._halfWidthStr);
                l1.setAttributeNS(null, "y1", TICK_OUTER.toString());
                l1.setAttributeNS(null, "x2", this._halfWidthStr);
                l1.setAttributeNS(null, "y2", y2.toString());
                l1.setAttributeNS(null, "stroke", s);
                l1.setAttributeNS(null, "stroke-width", w.toString());
                l1.setAttributeNS(
                    null,
                    "transform",
                    "rotate(" + i + `, ${this._halfWidth}, ${this._halfWidth})`
                );
                this._compassElement.appendChild(l1);

                // draw degree value every 30 degrees
                if (i % 30 == 0) {
                    const t1 = document.createElementNS(SVG_NS, "text");
                    if (i > 100) {
                        t1.setAttributeNS(null, "x", "130");
                    } else if (i > 0) {
                        t1.setAttributeNS(null, "x", "138");
                    } else if (i === 0) {
                        t1.setAttributeNS(null, "x", "144");
                    } else {
                        t1.setAttributeNS(null, "x", "132");
                    }
                    t1.setAttributeNS(null, "y", "24");
                    t1.setAttributeNS(null, "font-size", "1.5rem");
                    t1.setAttributeNS(null, "font-family", "Helvetica");
                    t1.setAttributeNS(null, "fill", "white");
                    t1.setAttributeNS(null, "style", "letter-spacing:1.0");
                    t1.setAttributeNS(
                        null,
                        "transform",
                        "rotate(" + i + "," + this._halfWidthStr + "," + this._halfWidthStr + ")"
                    );
                    const textNode = document.createTextNode(i.toString());
                    t1.appendChild(textNode);
                    this._compassElement.appendChild(t1);
                }
            }
        }
    }

    private _renderSVG() {
        return html` <svg
                id="compass-svg"
                viewBox="0 0 ${this._width} ${this._width}"
                xmlns="http://www.w3.org/2000/svg"
            ></svg>
            <svg
                id="pointer-svg"
                viewBox="0 0 ${this._width} ${this._width}"
                xmlns="http://www.w3.org/2000/svg"
            ></svg>`;
    }
    override render() {
        return html` <div class="compass" id="compass">${this._renderSVG()}</div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-compass": Compass;
    }
}
